import { useEffect, useState } from "react";
import { useAuth, API } from "../App";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  GraduationCap,
  Plus,
  Search,
  Pencil,
  Trash2,
  Loader2,
  BookOpen,
  FileText,
  Upload,
  Download,
  Settings,
  BarChart3,
  Calendar,
  Eye,
  Tag,
  Save,
  Edit,
  FolderOpen
} from "lucide-react";

const AdminCourses = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMainTab, setActiveMainTab] = useState("courses");
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    start_date: "",
    end_date: "",
    status: "active",
    assigned_professors: []
  });
  const [saving, setSaving] = useState(false);
  const [professors, setProfessors] = useState([]);

  // Quiz management state
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [quizType, setQuizType] = useState("pretest");
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [quizForm, setQuizForm] = useState({
    title: "",
    has_time_limit: true,
    time_limit: 30,
    max_attempts: 3,
    passing_score: 60,
    questions: []
  });
  const [currentQuestion, setCurrentQuestion] = useState({
    text: "",
    question_type: "single",
    options: ["", "", "", ""],
    correct_answers: [],
    points: 1
  });

  // Material management
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [showMaterialPreview, setShowMaterialPreview] = useState(false);
  const [previewMaterial, setPreviewMaterial] = useState(null);
  const [materialForm, setMaterialForm] = useState({
    title: "",
    description: "",
    material_type: "pdf",
    url: "",
    content: "",
    uploadMode: "url"  // "url" or "file"
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [categories, setCategories] = useState([]);

  // Post-creation prompt & import modal
  const [showPostCreatePrompt, setShowPostCreatePrompt] = useState(false);
  const [recentCourse, setRecentCourse] = useState(null);
  const [showQuizImportInfo, setShowQuizImportInfo] = useState(false);
  const [quizImportType, setQuizImportType] = useState(null);
  const [importQuizMeta, setImportQuizMeta] = useState({
    title: '',
    time_limit: 30,
    max_attempts: 1,
    passing_score: 70
  });
  const [importQuestionsFile, setImportQuestionsFile] = useState(null);

  // Quiz creation flow
  const [showQuizActionModal, setShowQuizActionModal] = useState(false);
  const [pendingQuizType, setPendingQuizType] = useState(null);

  // Category management state
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: "", description: "" });
  const [savingCategory, setSavingCategory] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");

  useEffect(() => {
    fetchCourses();
    fetchCategories();
    fetchProfessors();
  }, []);

  useEffect(() => {
    if (selectedCourse) {
      fetchCourseDetails();
    }
  }, [selectedCourse]);

  const fetchProfessors = async () => {
    try {
      const response = await axios.get(`${API}/users/professors`);
      setProfessors(response.data);
    } catch (error) {
      console.error("Error fetching professors:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleCreateCategory = async () => {
    if (!categoryForm.name) {
      toast.error("El nombre es requerido");
      return;
    }
    setSavingCategory(true);
    try {
      await axios.post(`${API}/categories`, categoryForm);
      toast.success("Categoría creada exitosamente");
      setShowCategoryModal(false);
      setCategoryForm({ name: "", description: "" });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear categoría");
    } finally {
      setSavingCategory(false);
    }
  };

  const handleUpdateCategory = async () => {
    setSavingCategory(true);
    try {
      await axios.put(`${API}/categories/${editingCategory.id}`, categoryForm);
      toast.success("Categoría actualizada");
      setEditingCategory(null);
      setCategoryForm({ name: "", description: "" });
      fetchCategories();
    } catch (error) {
      toast.error("Error al actualizar categoría");
    } finally {
      setSavingCategory(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("¿Está seguro de eliminar esta categoría?")) return;
    try {
      await axios.delete(`${API}/categories/${categoryId}`);
      toast.success("Categoría eliminada");
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar categoría");
    }
  };

  const openEditCategoryModal = (category) => {
    setEditingCategory(category);
    setCategoryForm({ name: category.name, description: category.description || "" });
  };

  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(categorySearch.toLowerCase())
  );

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`);
      setCourses(response.data);
    } catch (error) {
      toast.error("Error al cargar cursos");
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseDetails = async () => {
    console.log("🔍 Obteniendo detalles del curso:", selectedCourse.id);

    // Fetch materials
    try {
      const materialsRes = await axios.get(`${API}/materials?course_id=${selectedCourse.id}`);
      setMaterials(materialsRes.data);
      console.log("📦 Materials recibidos:", materialsRes.data.length);
    } catch (error) {
      console.warn("⚠️ No se encontraron materiales o error:", error.response?.status);
      setMaterials([]);
    }

    // Fetch quizzes
    try {
      const quizzesRes = await axios.get(`${API}/quizzes?course_id=${selectedCourse.id}`);
      console.log("📊 Quizzes recibidos:", quizzesRes.data);
      console.log("📊 Cantidad de quizzes:", quizzesRes.data.length);
      setQuizzes(quizzesRes.data);
    } catch (error) {
      console.error("❌ Error obteniendo quizzes:", error);
      setQuizzes([]);
    }
  };

  const handleCreateCourse = async () => {
    if (!formData.name || !formData.category || !formData.start_date || !formData.end_date) {
      toast.error("Complete todos los campos requeridos");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/courses`, formData);
      toast.success("Curso creado exitosamente");
      setRecentCourse(response.data);
      setShowCreateModal(false);
      setShowPostCreatePrompt(true);
      resetForm();
      fetchCourses();
    } catch (error) {

      toast.error(error.response?.data?.detail || "Error al crear curso");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCourse = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/courses/${editingCourse.id}`, formData);
      toast.success("Curso actualizado exitosamente");
      setEditingCourse(null);
      resetForm();
      fetchCourses();
    } catch (error) {
      toast.error("Error al actualizar curso");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm("¿Está seguro de eliminar este curso? Esto eliminará también las matriculaciones asociadas.")) return;

    try {
      await axios.delete(`${API}/courses/${courseId}`);
      toast.success("Curso eliminado");
      fetchCourses();
      if (selectedCourse?.id === courseId) {
        setSelectedCourse(null);
      }
    } catch (error) {
      toast.error("Error al eliminar curso");
    }
  };

  const openImportInfo = (course, type = null) => {
    const targetCourse = course || selectedCourse;
    setRecentCourse(targetCourse);
    setQuizImportType(type);
    setImportQuizMeta({
      title: type
        ? `${type === 'posttest' ? 'Post-test' : 'Pre-test'} ${targetCourse?.name || ''}`.trim()
        : `${targetCourse?.name || ''} - Nuevo cuestionario`.trim(),
      time_limit: 30,
      max_attempts: type === 'posttest' ? 1 : 1,
      passing_score: type === 'posttest' ? 80 : 60
    });
    setShowQuizImportInfo(true);
  };

  const handleDownloadQuizTemplate = async () => {
    try {
      const response = await axios.get(`${API}/quizzes/import-template`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_cuestionarios.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("No se pudo descargar la plantilla");
    }
  };

  const handleQuizBankFile = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImportQuestionsFile(file);
    } else {
      setImportQuestionsFile(null);
    }
  };

  const handleImportQuizFromFile = async () => {
    if (!importQuestionsFile) {
      toast.error("Por favor seleccione un archivo para importar");
      return;
    }

    if (!importQuizMeta.title) {
      toast.error("Por favor ingrese un título para el cuestionario");
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('course_id', recentCourse.id);
      formData.append('quiz_type', quizImportType || 'pretest');
      formData.append('title', importQuizMeta.title);
      formData.append('time_limit', importQuizMeta.time_limit);
      formData.append('max_attempts', importQuizMeta.max_attempts);
      formData.append('passing_score', importQuizMeta.passing_score);
      formData.append('file', importQuestionsFile);

      const response = await axios.post(`${API}/quizzes/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(response.data.message || "Cuestionario importado con éxito");
      setShowQuizImportInfo(false);
      setImportQuestionsFile(null);

      // Actualizar los detalles del curso para mostrar el nuevo cuestionario
      if (selectedCourse) {
        fetchCourseDetails();
      }
    } catch (error) {
      console.error("Error importing quiz:", error);
      toast.error(error.response?.data?.detail || "Error al importar el cuestionario");
    } finally {
      setSaving(false);
    }
  };

  const handleStartManualQuiz = (type) => {
    setPendingQuizType(null);
    setShowQuizActionModal(false);
    setQuizType(type);
    resetQuizForm();
    setShowQuizModal(true);
  };


  const handleCreateQuiz = async () => {
    if (!quizForm.title || quizForm.questions.length === 0) {
      toast.error("Agregue un título y al menos una pregunta");
      return;
    }

    setSaving(true);
    try {
      const quizData = {
        course_id: selectedCourse.id,
        quiz_type: quizType,
        title: quizForm.title,
        time_limit: quizForm.has_time_limit ? quizForm.time_limit : 0,
        max_attempts: quizForm.max_attempts,
        passing_score: quizForm.passing_score,
        randomize: quizForm.randomize || true,
        questions: quizForm.questions
      };

      if (editingQuiz) {
        await axios.put(`${API}/quizzes/${editingQuiz.id}`, quizData);
        toast.success("Cuestionario actualizado exitosamente");
      } else {
        await axios.post(`${API}/quizzes`, quizData);
        toast.success("Cuestionario creado exitosamente");
      }

      setShowQuizModal(false);
      setEditingQuiz(null);
      resetQuizForm();
      fetchCourseDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar cuestionario");
    } finally {
      setSaving(false);
    }
  };

  const addQuestion = () => {
    // Validar texto de la pregunta
    if (!currentQuestion.text) {
      toast.error("Escriba el texto de la pregunta");
      return;
    }

    // Validar opciones (excepto para true_false que ya tiene opciones predefinidas)
    if (currentQuestion.question_type !== 'true_false' && currentQuestion.options.filter(o => o).length < 2) {
      toast.error("Complete al menos 2 opciones de respuesta");
      return;
    }

    // Validar respuesta correcta
    if (currentQuestion.correct_answers.length === 0) {
      toast.error("Seleccione al menos una respuesta correcta");
      return;
    }

    // Para true_false, asegurar que las opciones sean correctas
    const questionToSave = currentQuestion.question_type === 'true_false'
      ? { ...currentQuestion, options: ['Verdadero', 'Falso'] }
      : { ...currentQuestion };

    setQuizForm(prev => ({
      ...prev,
      questions: [...prev.questions, questionToSave]
    }));

    // Reset para la siguiente pregunta
    setCurrentQuestion({
      text: "",
      question_type: "single",
      options: ["", "", "", ""],
      correct_answers: [],
      points: 1
    });

    toast.success(`Pregunta ${quizForm.questions.length + 1} guardada`);
  };

  // Vista previa de pregunta
  const [previewQuestion, setPreviewQuestion] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Edición inline de pregunta
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
  const [editingQuestionData, setEditingQuestionData] = useState(null);

  const handlePreviewQuestion = (question, index) => {
    // Parse JSON fields if they are strings (from PostgreSQL JSONB)
    const parsedQuestion = {
      ...question,
      number: index + 1,
      options: typeof question.options === 'string' ? JSON.parse(question.options) : question.options,
      correct_answers: typeof question.correct_answers === 'string' ? JSON.parse(question.correct_answers) : question.correct_answers
    };
    setPreviewQuestion(parsedQuestion);
    setShowPreview(true);
  };

  const handleEditQuestion = (question, index) => {
    // Parse JSON fields if they are strings (from PostgreSQL JSONB)
    const parsedQuestion = {
      ...question,
      options: typeof question.options === 'string' ? JSON.parse(question.options) : question.options,
      correct_answers: typeof question.correct_answers === 'string' ? JSON.parse(question.correct_answers) : question.correct_answers
    };
    setEditingQuestionIndex(index);
    setEditingQuestionData(parsedQuestion);
  };

  const handleSaveEditedQuestion = () => {
    // Validar texto de la pregunta
    if (!editingQuestionData.text) {
      toast.error("Escriba el texto de la pregunta");
      return;
    }

    // Validar opciones (excepto para true_false que ya tiene opciones predefinidas)
    if (editingQuestionData.question_type !== 'true_false' && editingQuestionData.options.filter(o => o).length < 2) {
      toast.error("Complete al menos 2 opciones de respuesta");
      return;
    }

    // Validar respuesta correcta
    if (editingQuestionData.correct_answers.length === 0) {
      toast.error("Seleccione al menos una respuesta correcta");
      return;
    }

    // Para true_false, asegurar que las opciones sean correctas
    const questionToSave = editingQuestionData.question_type === 'true_false'
      ? { ...editingQuestionData, options: ['Verdadero', 'Falso'] }
      : { ...editingQuestionData };

    const updatedQuestions = [...quizForm.questions];
    updatedQuestions[editingQuestionIndex] = questionToSave;
    setQuizForm(prev => ({ ...prev, questions: updatedQuestions }));

    setEditingQuestionIndex(null);
    setEditingQuestionData(null);
    toast.success("Pregunta actualizada");
  };

  const handleCancelEditQuestion = () => {
    setEditingQuestionIndex(null);
    setEditingQuestionData(null);
  };

  const handleDeleteQuestion = (index) => {
    if (!window.confirm(`¿Eliminar la Pregunta ${index + 1}?`)) return;
    setQuizForm(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
    toast.success("Pregunta eliminada");
  };

  const handleDeleteQuiz = async (quizId) => {
    if (!window.confirm("¿Está seguro de eliminar todo el cuestionario?")) return;
    try {
      await axios.delete(`${API}/quizzes/${quizId}`);
      toast.success("Cuestionario eliminado");
      fetchCourseDetails();
      setShowQuizModal(false);
      resetQuizForm();
    } catch (error) {
      toast.error("Error al eliminar cuestionario");
    }
  };

  const handleCreateMaterial = async () => {
    if (!materialForm.title) {
      toast.error("Ingrese un título");
      return;
    }

    setSaving(true);
    try {
      if (materialForm.uploadMode === "file" && selectedFile) {
        // Upload file
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('course_id', selectedCourse.id);
        formData.append('title', materialForm.title);
        formData.append('description', materialForm.description);

        await axios.post(`${API}/materials/upload?course_id=${selectedCourse.id}&title=${encodeURIComponent(materialForm.title)}&description=${encodeURIComponent(materialForm.description)}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        // Create with URL
        await axios.post(`${API}/materials`, {
          course_id: selectedCourse.id,
          title: materialForm.title,
          description: materialForm.description,
          material_type: materialForm.material_type,
          url: materialForm.url,
          content: materialForm.content
        });
      }
      toast.success("Material agregado exitosamente");
      setShowMaterialModal(false);
      resetMaterialForm();
      fetchCourseDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al agregar material");
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (50MB max)
      if (file.size > 50 * 1024 * 1024) {
        toast.error("El archivo no debe superar los 50MB");
        return;
      }
      setSelectedFile(file);
      // Auto-set title if empty
      if (!materialForm.title) {
        setMaterialForm(prev => ({ ...prev, title: file.name.replace(/\.[^/.]+$/, "") }));
      }
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    if (!window.confirm("¿Eliminar este material?")) return;
    try {
      await axios.delete(`${API}/materials/${materialId}`);
      toast.success("Material eliminado");
      fetchCourseDetails();
    } catch (error) {
      toast.error("Error al eliminar material");
    }
  };

  const handleEditQuiz = async (quiz) => {
    try {
      // Fetch full quiz details including questions
      const response = await axios.get(`${API}/quizzes/${quiz.id}`);
      const fullQuiz = response.data;

      // Parse JSON fields in questions (from PostgreSQL JSONB)
      const parsedQuestions = (fullQuiz.questions || []).map(q => ({
        ...q,
        options: typeof q.options === 'string' ? JSON.parse(q.options) : q.options,
        correct_answers: typeof q.correct_answers === 'string' ? JSON.parse(q.correct_answers) : q.correct_answers
      }));

      setEditingQuiz(fullQuiz);
      setQuizType(fullQuiz.quiz_type);
      setQuizForm({
        title: fullQuiz.title,
        has_time_limit: fullQuiz.time_limit > 0,
        time_limit: fullQuiz.time_limit || 30,
        max_attempts: fullQuiz.max_attempts,
        passing_score: fullQuiz.passing_score,
        questions: parsedQuestions
      });
      setShowQuizModal(true);
    } catch (error) {
      toast.error("Error al cargar cuestionario");
    }
  };

  const resetForm = () => {

    setFormData({
      name: "",
      description: "",
      category: "",
      start_date: "",
      end_date: "",
      status: "active",
      assigned_professors: []
    });
  };

  const resetQuizForm = () => {
    setQuizForm({
      title: "",
      has_time_limit: true,
      time_limit: 30,
      max_attempts: 3,
      passing_score: 60,
      questions: []
    });
    setCurrentQuestion({
      text: "",
      question_type: "single",
      options: ["", "", "", ""],
      correct_answers: [],
      points: 1
    });
    setEditingQuiz(null);
  };

  const resetMaterialForm = () => {
    setMaterialForm({
      title: "",
      description: "",
      material_type: "pdf",
      url: "",
      content: "",
      uploadMode: "url"
    });
    setSelectedFile(null);
  };

  const openEditModal = (course) => {
    setEditingCourse(course);
    setFormData({
      name: course.name,
      description: course.description,
      category: course.category,
      start_date: course.start_date,
      end_date: course.end_date,
      status: course.status,
      assigned_professors: course.assigned_professors || []
    });
  };

  const filteredCourses = courses.filter(course =>
    course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 w-48 skeleton rounded" />
        <div className="h-96 skeleton rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-courses-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-heading">Gestión de Cursos</h1>
          <p className="text-slate-600 mt-1">{courses.length} cursos • {categories.length} categorías</p>
        </div>
      </div>

      {/* Main Tabs: Cursos / Categorías */}
      <Tabs value={activeMainTab} onValueChange={setActiveMainTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="courses" className="gap-2">
            <GraduationCap className="w-4 h-4" />
            Cursos
          </TabsTrigger>
          <TabsTrigger value="categories" className="gap-2">
            <Tag className="w-4 h-4" />
            Categorías
          </TabsTrigger>
        </TabsList>

        {/* Cursos Tab */}
        <TabsContent value="courses">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setShowCreateModal(true)} data-testid="create-course-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Curso
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Course List */}
            <div className="lg:col-span-1 space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Buscar cursos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-courses-input"
                />
              </div>

              <div className="space-y-2 max-h-[600px] overflow-auto">
                {filteredCourses.map((course) => (
                  <Card
                    key={course.id}
                    className={`cursor-pointer transition-all ${selectedCourse?.id === course.id ? 'border-2 border-primary bg-primary/5' : 'hover:shadow-md'
                      }`}
                    onClick={() => setSelectedCourse(course)}
                    data-testid={`course-item-${course.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-slate-900 truncate">{course.name}</h4>
                          <p className="text-sm text-slate-500">{course.category}</p>
                        </div>
                        <Badge variant={course.status === 'active' ? 'default' : 'secondary'}>
                          {course.status}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Course Details */}
            <div className="lg:col-span-2">
              {selectedCourse ? (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="font-heading">{selectedCourse.name}</CardTitle>
                        <p className="text-slate-500 text-sm mt-1">{selectedCourse.description}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="icon" onClick={() => openEditModal(selectedCourse)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="icon" onClick={() => handleDeleteCourse(selectedCourse.id)} className="text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <Link to={`/courses/${selectedCourse.id}/reports`}>
                          <Button variant="outline" size="icon">
                            <BarChart3 className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Tabs defaultValue="content">
                      <TabsList className="mb-4">
                        <TabsTrigger value="content">Contenido</TabsTrigger>
                        <TabsTrigger value="quizzes">Cuestionarios</TabsTrigger>
                        <TabsTrigger value="settings">Configuración</TabsTrigger>
                      </TabsList>

                      <TabsContent value="content" className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium">Material de Estudio</h4>
                          <Button size="sm" onClick={() => setShowMaterialModal(true)} data-testid="add-material-btn">
                            <Plus className="w-4 h-4 mr-1" />
                            Agregar
                          </Button>
                        </div>
                        {materials.length === 0 ? (
                          <div className="text-center py-8 text-slate-500">
                            <BookOpen className="w-10 h-10 mx-auto mb-2 opacity-50" />
                            <p>No hay material agregado</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {materials.map((material) => (
                              <div key={material.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                <div className="flex items-center gap-3">
                                  <FileText className="w-5 h-5 text-slate-400" />
                                  <div>
                                    <p className="font-medium text-sm">{material.title}</p>
                                    <p className="text-xs text-slate-500">{material.material_type}</p>
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => {
                                      const getUrl = () => {
                                        if (material.url) return material.url;
                                        return `${API}/materials/${material.id}/view`;
                                      };
                                      const url = getUrl();

                                      if (material.material_type === 'pdf') {
                                        setPreviewMaterial({ ...material, fullUrl: url });
                                        setShowMaterialPreview(true);
                                      } else {
                                        window.open(url, '_blank');
                                      }
                                    }}
                                  >
                                    {material.material_type === 'pdf' ? (
                                      <Eye className="w-4 h-4 text-primary" />
                                    ) : (
                                      <Download className="w-4 h-4 text-slate-500" />
                                    )}
                                  </Button>
                                  <Button variant="ghost" size="icon" onClick={() => handleDeleteMaterial(material.id)}>
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </TabsContent>

                      <TabsContent value="quizzes" className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium">Cuestionarios</h4>
                          <div className="flex gap-2">
                            {!quizzes.find(q => q.quiz_type === 'pretest') && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setPendingQuizType('pretest');
                                  setShowQuizActionModal(true);
                                }}
                                data-testid="add-pretest-btn"
                              >
                                <Plus className="w-4 h-4 mr-1" />
                                Crear Pre-test
                              </Button>
                            )}
                            {!quizzes.find(q => q.quiz_type === 'posttest') && (
                              <Button
                                size="sm"
                                onClick={() => {
                                  setPendingQuizType('posttest');
                                  setShowQuizActionModal(true);
                                }}
                                data-testid="add-posttest-btn"
                              >
                                <Plus className="w-4 h-4 mr-1" />
                                Crear Post-test
                              </Button>
                            )}
                          </div>
                        </div>

                        {quizzes.length === 0 ? (
                          <div className="text-center py-8 text-slate-500">
                            <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                            <p>No hay cuestionarios creados</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {quizzes.map((quiz) => (
                              <div key={quiz.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <h5 className="font-medium">{quiz.title}</h5>
                                    <Badge variant={quiz.quiz_type === 'pretest' ? 'secondary' : 'default'}>
                                      {quiz.quiz_type}
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-slate-500 mt-1">
                                    {quiz.question_count} preguntas • {quiz.time_limit > 0 ? `${quiz.time_limit} min` : 'Sin límite'} • {quiz.passing_score}% mínimo
                                  </p>
                                </div>
                                <div className="flex gap-2">
                                  <Button variant="ghost" size="icon" onClick={() => handleEditQuiz(quiz)}>
                                    <Pencil className="w-4 h-4 text-primary" />
                                  </Button>
                                  <Button variant="ghost" size="icon" onClick={() => handleDeleteQuiz(quiz.id)}>
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </TabsContent>

                      <TabsContent value="settings" className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-slate-500">Categoría</Label>
                            <p className="font-medium">{selectedCourse.category}</p>
                          </div>
                          <div>
                            <Label className="text-slate-500">Estado</Label>
                            <p className="font-medium capitalize">{selectedCourse.status}</p>
                          </div>
                          <div>
                            <Label className="text-slate-500">Fecha Inicio</Label>
                            <p className="font-medium">{new Date(selectedCourse.start_date).toLocaleDateString()}</p>
                          </div>
                          <div>
                            <Label className="text-slate-500">Fecha Fin</Label>
                            <p className="font-medium">{new Date(selectedCourse.end_date).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              ) : (
                <Card className="h-full flex items-center justify-center">
                  <CardContent className="text-center py-12">
                    <GraduationCap className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">Selecciona un curso</h3>
                    <p className="text-slate-500">Elige un curso de la lista para ver sus detalles</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Categorías Tab */}
        <TabsContent value="categories">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Buscar categorías..."
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
                className="pl-10"
                data-testid="search-categories-input"
              />
            </div>
            <Button onClick={() => setShowCategoryModal(true)} data-testid="create-category-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Categoría
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead>Cursos</TableHead>
                  <TableHead>Fecha Creación</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCategories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                      <FolderOpen className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>No hay categorías disponibles</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCategories.map((category) => (
                    <TableRow key={category.id} data-testid={`category-row-${category.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Tag className="w-4 h-4 text-primary" />
                          <span className="font-medium">{category.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {category.description || '-'}
                      </TableCell>
                      <TableCell>
                        <span className="px-2 py-1 bg-slate-100 rounded text-sm">
                          {category.course_count || 0} cursos
                        </span>
                      </TableCell>
                      <TableCell>
                        {new Date(category.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditCategoryModal(category)}
                            data-testid={`edit-category-${category.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteCategory(category.id)}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-category-${category.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Course Modal */}
      <Dialog open={showCreateModal || !!editingCourse} onOpenChange={() => {
        setShowCreateModal(false);
        setEditingCourse(null);
        resetForm();
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCourse ? 'Editar Curso' : 'Crear Nuevo Curso'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre del Curso *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Introducción a Python"
                data-testid="course-name-field"
              />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descripción del curso..."
                data-testid="course-description-field"
              />
            </div>
            <div className="space-y-2">
              <Label>Categoría *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger data-testid="course-category-field">
                  <SelectValue placeholder="Seleccione una categoría" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {categories.length === 0 && (
                <p className="text-xs text-amber-600">No hay categorías. Crea una desde el menú Categorías.</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha Inicio *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  data-testid="course-start-date-field"
                />
              </div>
              <div className="space-y-2">
                <Label>Fecha Fin *</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  data-testid="course-end-date-field"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Estado</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
                <SelectTrigger data-testid="course-status-field">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Activo</SelectItem>
                  <SelectItem value="inactive">Inactivo</SelectItem>
                  <SelectItem value="draft">Borrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {user?.role === 'ADMIN' && (
              <div className="space-y-2">
                <Label>Profesores Asignados</Label>
                <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                  {professors.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-2">No hay profesores registrados</p>
                  ) : (
                    professors.map((prof) => (
                      <label
                        key={prof.id}
                        className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.assigned_professors.includes(prof.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData(prev => ({
                                ...prev,
                                assigned_professors: [...prev.assigned_professors, prof.id]
                              }));
                            } else {
                              setFormData(prev => ({
                                ...prev,
                                assigned_professors: prev.assigned_professors.filter(id => id !== prof.id)
                              }));
                            }
                          }}
                          className="w-4 h-4 rounded border-slate-300"
                        />
                        <div>
                          <p className="text-sm font-medium">{prof.full_name}</p>
                          <p className="text-xs text-slate-500">{prof.username}</p>
                        </div>
                      </label>
                    ))
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  {formData.assigned_professors.length} profesor(es) seleccionado(s)
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateModal(false); setEditingCourse(null); resetForm(); }}>
              Cancelar
            </Button>
            <Button onClick={editingCourse ? handleUpdateCourse : handleCreateCourse} disabled={saving} data-testid="save-course-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingCourse ? 'Guardar Cambios' : 'Crear Curso'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showQuizActionModal && !!pendingQuizType} onOpenChange={(open) => {
        setShowQuizActionModal(open);
        if (!open) {
          setPendingQuizType(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {pendingQuizType === 'posttest' ? 'Configurar Post-test' : 'Configurar Pre-test'}
            </DialogTitle>
            <p className="text-sm text-slate-500">
              ¿Cómo deseas crear este cuestionario para {selectedCourse?.name || 'el curso seleccionado'}?
            </p>
          </DialogHeader>
          <div className="grid gap-3">
            <Button onClick={() => handleStartManualQuiz(pendingQuizType)}>
              Diligenciar cuestionario
            </Button>
            <Button
              variant="outline"
              disabled={!selectedCourse}
              onClick={() => {
                if (!selectedCourse) return;
                setShowQuizActionModal(false);
                openImportInfo(selectedCourse, pendingQuizType);
              }}
            >
              Importar cuestionario
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowQuizActionModal(false); setPendingQuizType(null); }}>
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showPostCreatePrompt && !!recentCourse} onOpenChange={setShowPostCreatePrompt}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Curso creado</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            El curso <span className="font-semibold">{recentCourse?.name}</span> ya está listo. ¿Deseas continuar importando un cuestionario (pre o post-test) para este curso usando la plantilla masiva?
          </p>
          <DialogFooter className="gap-3 sm:justify-end">
            <Button variant="outline" onClick={() => setShowPostCreatePrompt(false)}>
              Más tarde
            </Button>
            <Button onClick={() => {
              setShowPostCreatePrompt(false);
              openImportInfo(recentCourse);
            }}>
              Importar cuestionario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showQuizImportInfo} onOpenChange={(open) => {
        setShowQuizImportInfo(open);
        if (!open) {
          setQuizImportType(null);
        }
      }}>
        <DialogContent className="max-w-xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Importar cuestionario</DialogTitle>
            <p className="text-sm text-slate-500">
              Completa los datos generales del {quizImportType === 'posttest' ? 'post-test' : quizImportType === 'pretest' ? 'pre-test' : 'cuestionario'} y luego adjunta el banco de preguntas (selección múltiple o verdadero/falso).
            </p>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-2 px-1">
            <div className="space-y-6 py-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Título del cuestionario *</Label>
                  <Input
                    value={importQuizMeta.title}
                    onChange={(e) => setImportQuizMeta(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Ej. Pre-test Seguridad Industrial"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tiempo límite (minutos)</Label>
                    <Input
                      type="number"
                      min={0}
                      value={importQuizMeta.time_limit}
                      onChange={(e) => setImportQuizMeta(prev => ({ ...prev, time_limit: Number(e.target.value) }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Intentos máximos</Label>
                    <Input
                      type="number"
                      min={1}
                      value={importQuizMeta.max_attempts}
                      onChange={(e) => setImportQuizMeta(prev => ({ ...prev, max_attempts: Number(e.target.value) }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nota mínima aprobatoria (%)</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={importQuizMeta.passing_score}
                      onChange={(e) => setImportQuizMeta(prev => ({ ...prev, passing_score: Number(e.target.value) }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo de cuestionario</Label>
                    <Input value={quizImportType || 'No especificado'} disabled />
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Banco de preguntas (.xlsx o .csv)</Label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-emerald-500 transition-colors">
                  <input
                    type="file"
                    accept=".xlsx,.csv"
                    id="quiz-bank-upload"
                    className="hidden"
                    onChange={handleQuizBankFile}
                  />
                  <label htmlFor="quiz-bank-upload" className="cursor-pointer block">
                    {importQuestionsFile ? (
                      <div className="space-y-1">
                        <p className="font-medium">{importQuestionsFile.name}</p>
                        <p className="text-xs text-slate-500">{(importQuestionsFile.size / 1024 / 1024).toFixed(2)} MB</p>
                        <p className="text-xs text-emerald-600">Clic para cambiar archivo</p>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <Upload className="w-6 h-6 mx-auto text-slate-400" />
                        <p className="text-sm text-slate-600">Clic para seleccionar archivo</p>
                        <p className="text-xs text-slate-400">Plantilla basada solo en preguntas</p>
                      </div>
                    )}
                  </label>
                </div>
                <div className="rounded-lg border border-dashed border-slate-200 p-4 bg-slate-50 text-sm text-slate-600">
                  <p className="font-semibold mb-3">Formato del archivo (una fila = una pregunta):</p>
                  <div className="space-y-2">
                    <p><strong>question_text</strong> → escribe el enunciado completo de la pregunta.</p>
                    <p><strong>question_type</strong> → usa <code>multiple_choice</code> para selección múltiple o <code>true_false</code> para verdadero/falso.</p>
                    <p><strong>option_1</strong> a <strong>option_4</strong> → llena solo las opciones que necesites (se ignoran las vacías).</p>
                    <p><strong>correct_options</strong> → indica el número de la opción correcta separado por comas si hay varias (ej. <code>2</code> o <code>1,3</code>). Aplica solo para selección múltiple.</p>
                    <p><strong>answer_true_false</strong> → escribe <code>TRUE</code> o <code>FALSE</code> cuando la pregunta sea de verdadero/falso.</p>
                  </div>
                  <p className="mt-3 text-xs text-slate-500">Si usas CSV, respeta estos encabezados y separa con comas.</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="justify-between w-full"
                  onClick={handleDownloadQuizTemplate}
                >
                  Descargar plantilla XLSX
                  <Download className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter className="pt-4 border-t mt-auto flex justify-between gap-2">
            <Button variant="outline" onClick={() => setShowQuizImportInfo(false)}>Cancelar</Button>
            <Button
              onClick={handleImportQuizFromFile}
              disabled={saving || !importQuestionsFile}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Importar Ahora
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showQuizModal} onOpenChange={() => { setShowQuizModal(false); resetQuizForm(); }}>

        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{editingQuiz ? 'Editar' : 'Crear'} {quizType === 'pretest' ? 'Pre-test' : 'Post-test'}</DialogTitle>
            <DialogDescription>
              Configura los detalles del cuestionario y agrega las preguntas necesarias.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-2">
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label>Título del Cuestionario *</Label>
                  <Input
                    value={quizForm.title}
                    onChange={(e) => setQuizForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Evaluación de conocimientos previos"
                    data-testid="quiz-title-field"
                  />
                </div>

                {/* Time Limit Options */}
                <div className="space-y-2 col-span-2">
                  <Label>Tiempo límite</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={!quizForm.has_time_limit ? "default" : "outline"}
                      className="flex-1"
                      onClick={() => setQuizForm(prev => ({ ...prev, has_time_limit: false }))}
                    >
                      Sin tiempo límite
                    </Button>
                    <Button
                      type="button"
                      variant={quizForm.has_time_limit ? "default" : "outline"}
                      className="flex-1"
                      onClick={() => setQuizForm(prev => ({ ...prev, has_time_limit: true }))}
                    >
                      Con tiempo límite
                    </Button>
                  </div>
                </div>

                {quizForm.has_time_limit && (
                  <div className="space-y-2 col-span-2">
                    <Label>Duración (minutos)</Label>
                    <Input
                      type="number"
                      value={quizForm.time_limit}
                      onChange={(e) => setQuizForm(prev => ({ ...prev, time_limit: parseInt(e.target.value) }))}
                      min="1"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Intentos máximos</Label>
                  <Input
                    type="number"
                    value={quizForm.max_attempts}
                    onChange={(e) => setQuizForm(prev => ({ ...prev, max_attempts: parseInt(e.target.value) }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nota mínima aprobatoria (%)</Label>
                  <Input
                    type="number"
                    value={quizForm.passing_score}
                    onChange={(e) => setQuizForm(prev => ({ ...prev, passing_score: parseFloat(e.target.value) }))}
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Pregunta {quizForm.questions.length + 1}</h4>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Texto de la Pregunta *</Label>
                    <Textarea
                      value={currentQuestion.text}
                      onChange={(e) => setCurrentQuestion(prev => ({ ...prev, text: e.target.value }))}
                      placeholder={`Escribe la pregunta ${quizForm.questions.length + 1}...`}
                      data-testid="question-text-field"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo de Pregunta</Label>
                    <Select
                      value={currentQuestion.question_type}
                      onValueChange={(value) => setCurrentQuestion(prev => ({
                        ...prev,
                        question_type: value,
                        correct_answers: [],
                        options: value === 'true_false' ? ['Verdadero', 'Falso'] : ['', '', '', '']
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single">Selección única</SelectItem>
                        <SelectItem value="multiple">Selección múltiple</SelectItem>
                        <SelectItem value="true_false">Verdadero/Falso</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Opciones de respuesta</Label>
                    {currentQuestion.question_type === 'true_false' ? (
                      <div className="space-y-2">
                        {['Verdadero', 'Falso'].map((opt, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <input
                              type="radio"
                              checked={currentQuestion.correct_answers.includes(idx)}
                              onChange={() => setCurrentQuestion(prev => ({ ...prev, correct_answers: [idx] }))}
                              className="w-4 h-4"
                            />
                            <span>{opt}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      currentQuestion.options.map((opt, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <input
                            type={currentQuestion.question_type === 'single' ? 'radio' : 'checkbox'}
                            checked={currentQuestion.correct_answers.includes(idx)}
                            onChange={() => {
                              if (currentQuestion.question_type === 'single') {
                                setCurrentQuestion(prev => ({ ...prev, correct_answers: [idx] }));
                              } else {
                                setCurrentQuestion(prev => ({
                                  ...prev,
                                  correct_answers: prev.correct_answers.includes(idx)
                                    ? prev.correct_answers.filter(i => i !== idx)
                                    : [...prev.correct_answers, idx]
                                }));
                              }
                            }}
                            className="w-4 h-4"
                          />
                          <Input
                            value={opt}
                            onChange={(e) => {
                              const newOptions = [...currentQuestion.options];
                              newOptions[idx] = e.target.value;
                              setCurrentQuestion(prev => ({ ...prev, options: newOptions }));
                            }}
                            placeholder={`Opción ${idx + 1}`}
                            className="flex-1"
                          />
                        </div>
                      ))
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" onClick={addQuestion} data-testid="add-question-btn" className="flex-1">
                      <Save className="w-4 h-4 mr-2" />
                      Guardar Pregunta {quizForm.questions.length + 1}
                    </Button>
                    {currentQuestion.text && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handlePreviewQuestion(currentQuestion, quizForm.questions.length)}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Vista Previa
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {quizForm.questions.length > 0 && (
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium">Preguntas Guardadas ({quizForm.questions.length})</h4>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (window.confirm("¿Eliminar todas las preguntas?")) {
                          setQuizForm(prev => ({ ...prev, questions: [] }));
                          toast.success("Preguntas eliminadas");
                        }
                      }}
                      className="text-red-500 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Eliminar Todas
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {quizForm.questions.map((q, idx) => (
                      <div key={idx} className={`p-4 rounded-lg border-2 transition-all ${editingQuestionIndex === idx ? 'bg-blue-50 border-blue-300' : 'bg-slate-50 border-slate-200'}`}>
                        {editingQuestionIndex === idx ? (
                          /* Modo Edición Inline */
                          <div className="space-y-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-semibold text-blue-700">✏️ Editando Pregunta {idx + 1}</h4>
                              <Badge variant="secondary">Modo Edición</Badge>
                            </div>

                            {/* Texto de la pregunta */}
                            <div className="space-y-2">
                              <Label>Texto de la Pregunta *</Label>
                              <Textarea
                                value={editingQuestionData.text}
                                onChange={(e) => setEditingQuestionData(prev => ({ ...prev, text: e.target.value }))}
                                placeholder="Escribe la pregunta..."
                                rows={2}
                              />
                            </div>

                            {/* Tipo de pregunta */}
                            <div className="space-y-2">
                              <Label>Tipo de Pregunta</Label>
                              <Select
                                value={editingQuestionData.question_type}
                                onValueChange={(value) => setEditingQuestionData(prev => ({
                                  ...prev,
                                  question_type: value,
                                  correct_answers: [],
                                  options: value === 'true_false' ? ['Verdadero', 'Falso'] : ['', '', '', '']
                                }))}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="single">Selección única</SelectItem>
                                  <SelectItem value="multiple">Selección múltiple</SelectItem>
                                  <SelectItem value="true_false">Verdadero/Falso</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Opciones de respuesta */}
                            <div className="space-y-2">
                              <Label>Opciones de respuesta</Label>
                              {editingQuestionData.question_type === 'true_false' ? (
                                <div className="space-y-2">
                                  {['Verdadero', 'Falso'].map((opt, optIdx) => (
                                    <div key={optIdx} className="flex items-center gap-3 p-2 bg-white rounded">
                                      <input
                                        type="radio"
                                        checked={editingQuestionData.correct_answers.includes(optIdx)}
                                        onChange={() => setEditingQuestionData(prev => ({ ...prev, correct_answers: [optIdx] }))}
                                        className="w-4 h-4"
                                      />
                                      <span className="font-medium">{opt}</span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                editingQuestionData.options.map((opt, optIdx) => (
                                  <div key={optIdx} className="flex items-center gap-3">
                                    <input
                                      type={editingQuestionData.question_type === 'single' ? 'radio' : 'checkbox'}
                                      checked={editingQuestionData.correct_answers.includes(optIdx)}
                                      onChange={() => {
                                        if (editingQuestionData.question_type === 'single') {
                                          setEditingQuestionData(prev => ({ ...prev, correct_answers: [optIdx] }));
                                        } else {
                                          setEditingQuestionData(prev => ({
                                            ...prev,
                                            correct_answers: prev.correct_answers.includes(optIdx)
                                              ? prev.correct_answers.filter(i => i !== optIdx)
                                              : [...prev.correct_answers, optIdx]
                                          }));
                                        }
                                      }}
                                      className="w-4 h-4"
                                    />
                                    <Input
                                      value={opt}
                                      onChange={(e) => {
                                        const newOptions = [...editingQuestionData.options];
                                        newOptions[optIdx] = e.target.value;
                                        setEditingQuestionData(prev => ({ ...prev, options: newOptions }));
                                      }}
                                      placeholder={`Opción ${optIdx + 1}`}
                                      className="flex-1"
                                    />
                                  </div>
                                ))
                              )}
                            </div>

                            {/* Botones de acción */}
                            <div className="flex gap-2 pt-2">
                              <Button
                                type="button"
                                onClick={handleSaveEditedQuestion}
                                size="sm"
                              >
                                <Save className="w-3 h-3 mr-1" />
                                Guardar Cambios
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => handlePreviewQuestion(editingQuestionData, idx)}
                                size="sm"
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                Vista Previa
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                onClick={handleCancelEditQuestion}
                                size="sm"
                              >
                                Cancelar
                              </Button>
                            </div>
                          </div>
                        ) : (
                          /* Modo Vista Normal */
                          <>
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <p className="font-medium text-sm">Pregunta {idx + 1}</p>
                                <p className="text-sm mt-1">{q.text}</p>
                                <p className="text-xs text-slate-500 mt-1">
                                  {q.question_type === 'single' ? 'Selección única' :
                                    q.question_type === 'multiple' ? 'Selección múltiple' :
                                      'Verdadero/Falso'}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2 mt-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditQuestion(q, idx)}
                              >
                                <Edit className="w-3 h-3 mr-1" />
                                Editar
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => handlePreviewQuestion(q, idx)}
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                Vista Previa
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteQuestion(idx)}
                                className="text-red-500 hover:text-red-600"
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                Eliminar
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="flex justify-between pt-4 border-t">
            <div>
              {editingQuiz && (
                <Button
                  variant="destructive"
                  onClick={() => handleDeleteQuiz(editingQuiz.id)}
                  disabled={saving}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar Cuestionario
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setShowQuizModal(false); resetQuizForm(); }}>
                Cancelar
              </Button>
              <Button onClick={handleCreateQuiz} disabled={saving} data-testid="save-quiz-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingQuiz ? 'Guardar Cambios' : 'Crear Cuestionario'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Material Modal */}
      <Dialog open={showMaterialModal} onOpenChange={() => { setShowMaterialModal(false); resetMaterialForm(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Agregar Material</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Upload Mode Selection */}
            <div className="space-y-2">
              <Label>Método de carga</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={materialForm.uploadMode === "url" ? "default" : "outline"}
                  className="flex-1"
                  onClick={() => setMaterialForm(prev => ({ ...prev, uploadMode: "url" }))}
                >
                  URL / Enlace
                </Button>
                <Button
                  type="button"
                  variant={materialForm.uploadMode === "file" ? "default" : "outline"}
                  className="flex-1"
                  onClick={() => setMaterialForm(prev => ({ ...prev, uploadMode: "file" }))}
                >
                  Subir Archivo
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Título *</Label>
              <Input
                value={materialForm.title}
                onChange={(e) => setMaterialForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Guía de introducción"
                data-testid="material-title-field"
              />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea
                value={materialForm.description}
                onChange={(e) => setMaterialForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descripción del material..."
              />
            </div>

            {materialForm.uploadMode === "file" ? (
              /* File Upload Mode */
              <div className="space-y-2">
                <Label>Archivo (PDF, Word, Excel, PowerPoint)</Label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-emerald-500 transition-colors">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="material-file-upload"
                  />
                  <label htmlFor="material-file-upload" className="cursor-pointer">
                    {selectedFile ? (
                      <div className="space-y-2">
                        <FileText className="w-10 h-10 mx-auto text-emerald-500" />
                        <p className="font-medium text-slate-700">{selectedFile.name}</p>
                        <p className="text-sm text-slate-500">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                        <p className="text-xs text-emerald-600">Clic para cambiar archivo</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Upload className="w-10 h-10 mx-auto text-slate-400" />
                        <p className="text-slate-600">Clic para seleccionar archivo</p>
                        <p className="text-xs text-slate-400">PDF, Word, Excel, PowerPoint (máx. 50MB)</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>
            ) : (
              /* URL Mode */
              <>
                <div className="space-y-2">
                  <Label>Tipo de Material</Label>
                  <Select
                    value={materialForm.material_type}
                    onValueChange={(value) => setMaterialForm(prev => ({ ...prev, material_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pdf">PDF</SelectItem>
                      <SelectItem value="video">Video</SelectItem>
                      <SelectItem value="audio">Audio</SelectItem>
                      <SelectItem value="link">Enlace externo</SelectItem>
                      <SelectItem value="document">Documento</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>URL del recurso</Label>
                  <Input
                    value={materialForm.url}
                    onChange={(e) => setMaterialForm(prev => ({ ...prev, url: e.target.value }))}
                    placeholder="https://ejemplo.com/recurso.pdf"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contenido adicional</Label>
                  <Textarea
                    value={materialForm.content}
                    onChange={(e) => setMaterialForm(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Texto o descripción detallada del material..."
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowMaterialModal(false); resetMaterialForm(); }}>
              Cancelar
            </Button>
            <Button onClick={handleCreateMaterial} disabled={saving} data-testid="save-material-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Agregar Material
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Question Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Vista Previa - Pregunta {previewQuestion?.number}</DialogTitle>
          </DialogHeader>
          {previewQuestion && (
            <div className="py-6 space-y-4">
              <div className="bg-slate-50 p-6 rounded-lg">
                <h3 className="font-semibold text-lg mb-4">
                  {previewQuestion.number}. {previewQuestion.text}
                </h3>
                <div className="space-y-3 ml-4">
                  {previewQuestion.question_type === 'true_false' ? (
                    ['Verdadero', 'Falso'].map((opt, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-white rounded border border-slate-200 hover:border-emerald-400 transition-colors">
                        <div className="w-5 h-5 rounded-full border-2 border-slate-300 flex items-center justify-center">
                          {previewQuestion.correct_answers.includes(idx) && (
                            <div className="w-3 h-3 rounded-full bg-emerald-500" />
                          )}
                        </div>
                        <span className={previewQuestion.correct_answers.includes(idx) ? "font-medium text-emerald-600" : ""}>
                          {opt}
                        </span>
                      </div>
                    ))
                  ) : (
                    previewQuestion.options.filter(opt => opt).map((opt, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-white rounded border border-slate-200 hover:border-emerald-400 transition-colors">
                        <div className={`w-5 h-5 ${previewQuestion.question_type === 'single' ? 'rounded-full' : 'rounded'} border-2 border-slate-300 flex items-center justify-center`}>
                          {previewQuestion.correct_answers.includes(idx) && (
                            <div className={`w-3 h-3 ${previewQuestion.question_type === 'single' ? 'rounded-full' : 'rounded'} bg-emerald-500`} />
                          )}
                        </div>
                        <span className={previewQuestion.correct_answers.includes(idx) ? "font-medium text-emerald-600" : ""}>
                          {opt}
                        </span>
                      </div>
                    ))
                  )}
                </div>
                <div className="mt-4 pt-4 border-t border-slate-200 text-sm text-slate-600">
                  <p><strong>Tipo:</strong> {
                    previewQuestion.question_type === 'single' ? 'Selección única' :
                      previewQuestion.question_type === 'multiple' ? 'Selección múltiple' :
                        'Verdadero/Falso'
                  }</p>
                  <p><strong>Puntos:</strong> {previewQuestion.points}</p>
                  <p className="text-emerald-600 mt-2">
                    <strong>Respuesta(s) correcta(s):</strong> {
                      previewQuestion.question_type === 'true_false'
                        ? (previewQuestion.correct_answers.includes(0) ? 'Verdadero' : 'Falso')
                        : previewQuestion.correct_answers.map(idx =>
                          previewQuestion.options[idx] || `Opción ${idx + 1}`
                        ).join(', ')
                    }
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowPreview(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Category Modal */}
      <Dialog open={showCategoryModal || !!editingCategory} onOpenChange={() => {
        setShowCategoryModal(false);
        setEditingCategory(null);
        setCategoryForm({ name: "", description: "" });
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej: Programación, Marketing, Idiomas..."
                data-testid="category-name-field"
              />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea
                value={categoryForm.description}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descripción opcional de la categoría..."
                data-testid="category-description-field"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCategoryModal(false);
              setEditingCategory(null);
              setCategoryForm({ name: "", description: "" });
            }}>
              Cancelar
            </Button>
            <Button
              onClick={editingCategory ? handleUpdateCategory : handleCreateCategory}
              disabled={savingCategory}
              data-testid="save-category-btn"
            >
              {savingCategory && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingCategory ? 'Guardar Cambios' : 'Crear Categoría'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Material Preview Modal */}
      <Dialog open={showMaterialPreview} onOpenChange={() => {
        setShowMaterialPreview(false);
        setPreviewMaterial(null);
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{previewMaterial?.title}</DialogTitle>
            <DialogDescription>
              Vista previa del material de estudio seleccionado.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto min-h-[400px] flex flex-col">
            {previewMaterial && (
              <>
                <div className="flex justify-end px-4 pb-2">
                  <Button variant="outline" size="sm" onClick={() => window.open(previewMaterial.fullUrl, '_blank')}>
                    <Download className="w-4 h-4 mr-2" />
                    Descargar / Abrir en nueva ventana
                  </Button>
                </div>
                <iframe
                  src={previewMaterial.fullUrl}
                  className="w-full flex-1 min-h-[600px] border-none rounded-lg bg-slate-100"
                  title="PDF Preview"
                />
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCourses;
